import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Loader2,
  Building2,
  Mail,
  Phone,
  ShieldCheck,
  BarChart3,
  DollarSign,
  Users2,
  Calculator,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';

const toSafeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) =>
  `$${toSafeNumber(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatInteger = (value) =>
  toSafeNumber(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

const formatPercent = (value) =>
  `${toSafeNumber(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const HowItWorksFlow = () => {
  const steps = [
    { icon: '💰', title: 'Pre-Tax Deduction', description: 'Employee elects to set aside portion of salary', color: 'bg-blue-50 border-blue-200' },
    { icon: '📉', title: 'Tax Savings', description: 'Reduces federal, FICA & state taxes', color: 'bg-emerald-50 border-emerald-200' },
    { icon: '🏥', title: 'Benefit Access', description: 'Use funds for health, dependent care, transit', color: 'bg-purple-50 border-purple-200' },
    { icon: '✅', title: 'Your Net Benefit', description: 'More take-home pay, lower employer FICA', color: 'bg-amber-50 border-amber-200' },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">How Section 125 Works</h3>
      </div>
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
        {steps.map((step, idx) => (
          <React.Fragment key={step.title}>
            <div className={`flex-1 rounded-lg p-4 border-2 ${step.color} text-center`}>
              <span className="text-2xl mb-2 block">{step.icon}</span>
              <p className="font-semibold text-slate-800 text-sm">{step.title}</p>
              <p className="text-xs text-slate-600 mt-1 hidden md:block">{step.description}</p>
            </div>
            {idx < steps.length - 1 && (
              <div className="hidden md:flex items-center justify-center w-8">
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const AverageEmployeeImpact = ({ summary }) => {
  if (!summary) return null;
  const avgMonthlyFlex = toSafeNumber(summary.estimatedAvgMonthlyFlex);
  const avgFederalSavings = toSafeNumber(summary.estimatedAvgMonthlyFederalTaxSavings);
  const avgFicaSavings = toSafeNumber(summary.estimatedAvgMonthlyFicaTaxSavings);
  const hasSummaryTaxSplit = avgFederalSavings > 0 || avgFicaSavings > 0;
  const estimatedFederalAndFica = avgMonthlyFlex * 0.2965;
  const displayFederal = hasSummaryTaxSplit ? avgFederalSavings : estimatedFederalAndFica * 0.72;
  const displayFica = hasSummaryTaxSplit ? avgFicaSavings : estimatedFederalAndFica * 0.28;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-6 border border-emerald-200">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">Average Employee Impact</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-emerald-600">{formatCurrency(avgMonthlyFlex)}</p>
          <p className="text-xs text-slate-600 mt-1">Monthly Premium Credit</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-blue-600">{formatCurrency(displayFederal)}</p>
          <p className="text-xs text-slate-600 mt-1">Federal Tax Savings</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-purple-600">{formatCurrency(displayFica)}</p>
          <p className="text-xs text-slate-600 mt-1">FICA Savings</p>
        </div>
      </div>
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800 text-center">
          <span className="font-semibold">💡 Tip:</span> Employees save ~30% in taxes on every dollar set aside through Section 125
        </p>
      </div>
    </div>
  );
};

const KeyMetricsCards = ({ summary }) => {
  const cards = [
    { label: 'Total Employees', value: summary?.totalRows || 0, type: 'integer', icon: Users2, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Monthly Net Savings', value: summary?.estimatedNetMonthlySavings || 0, type: 'currency', icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Annual Net Savings', value: summary?.estimatedNetAnnualSavings || 0, type: 'currency', icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Avg Monthly Premium', value: summary?.estimatedAvgMonthlyFlex || 0, type: 'currency', icon: Calculator, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">Key Metrics</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          const display = card.type === 'currency' ? formatCurrency(card.value) : formatInteger(card.value);
          return (
            <div key={card.label} className={`${card.bg} rounded-lg p-3 sm:p-4 text-center`}>
              <Icon className={`w-5 h-5 ${card.color} mx-auto mb-2`} />
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{display}</p>
              <p className="text-xs text-slate-600 mt-1">{card.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function ProposalPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProposal = async () => {
      try {
        const response = await apiClient.get(`/public/proposals/${token}`);
        setProposal(response.data?.proposal || null);
      } catch (err) {
        console.error('[ProposalPublic] loadProposal error:', err);
        setError('Proposal not found');
      } finally {
        setLoading(false);
      }
    };

    if (token) loadProposal();
    else {
      setLoading(false);
      setError('Invalid proposal link');
    }
  }, [token]);

  const summary = proposal?.proposal_summary || {};
  const rows = proposal?.census_rows || [];

  const flattenedRows = useMemo(() => rows.map((row) => {
    const raw = row.raw_data || {};
    const computed = row.computed_data || {};
    return {
      id: row.id,
      full_name: row.full_name || raw.full_name || raw.name || 'Unknown',
      annual_salary: Number(raw.annual_salary || 0),
      filing_status: raw.filing_status || '',
      state: raw.state || '',
      w4_version: raw.w4_version || '',
      w4_allowances: raw.w4_allowances ?? 0,
      w4_step3_total: Number(raw.w4_step3_total || 0),
      pre_tax_monthly: Number(raw.pre_tax_monthly || 0),
      section125_monthly: Number(computed.section125_monthly ?? computed.section125 ?? 0),
      federal_tax_savings: Number(computed.federal_tax_savings || computed.federal_savings || 0),
      fica_tax_savings: Number(computed.fica_tax_savings || computed.fica_savings || 0),
      state_tax_savings: Number(computed.state_tax_savings || computed.state_savings || 0),
      total_tax_savings: Number(
        computed.total_tax_savings ||
        computed.total_savings ||
        (computed.federal_tax_savings || computed.federal_savings || 0) +
          (computed.fica_tax_savings || computed.fica_savings || 0) +
          (computed.state_tax_savings || computed.state_savings || 0)
      ),
      net_flex: Number(computed.net_flex || 0),
      employer_gross: Number(computed.employer_gross || 0),
      fee: Number(computed.fee || computed.employer_fee || 0),
      contribution: Number(computed.contribution || 0),
      employer_net: Number(computed.employer_net || 0),
      annualized: Number(computed.annualized || (computed.employer_net || 0) * 12),
    };
  }), [rows]);

  const metrics = useMemo(() => {
    const monthlyFederal = flattenedRows.reduce((sum, row) => sum + row.federal_tax_savings, 0);
    const monthlyFica = flattenedRows.reduce((sum, row) => sum + row.fica_tax_savings, 0);
    const monthlyState = flattenedRows.reduce((sum, row) => sum + row.state_tax_savings, 0);
    const monthlyEmployeeTaxSavings = flattenedRows.reduce((sum, row) => sum + row.total_tax_savings, 0);
    const monthlyFee = flattenedRows.reduce((sum, row) => sum + row.fee, 0);
    const monthlyContrib = flattenedRows.reduce((sum, row) => sum + row.contribution, 0);
    const monthlyEmployerNet = flattenedRows.reduce((sum, row) => sum + row.employer_net, 0);
    const annualTotalTaxSavings = (monthlyFederal + monthlyFica + monthlyState) * 12;
    const employeeCount = Number(summary.totalRows || flattenedRows.length || 0);

    return {
      employeeCount,
      monthlyFederal,
      monthlyFica,
      monthlyState,
      monthlyEmployeeTaxSavings,
      monthlyFee,
      monthlyContrib,
      monthlyEmployerNet,
      annualTotalTaxSavings,
      federalPct: annualTotalTaxSavings > 0 ? ((monthlyFederal * 12) / annualTotalTaxSavings) * 100 : 0,
      ficaPct: annualTotalTaxSavings > 0 ? ((monthlyFica * 12) / annualTotalTaxSavings) * 100 : 0,
      statePct: annualTotalTaxSavings > 0 ? ((monthlyState * 12) / annualTotalTaxSavings) * 100 : 0,
    };
  }, [flattenedRows, summary.totalRows]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  if (!proposal) return <div className="min-h-screen flex items-center justify-center"><Card className="max-w-md"><CardHeader><CardTitle>Proposal Unavailable</CardTitle></CardHeader><CardContent><p>{error || 'We could not locate this proposal.'}</p></CardContent></Card></div>;

  const monthlyCombined = metrics.monthlyFederal + metrics.monthlyFica + metrics.monthlyState;
  const monthlyGrossBenefit = metrics.monthlyFica - metrics.monthlyFee;
  const monthlyNetBenefit = monthlyGrossBenefit - metrics.monthlyContrib;
  const annualInvestment = metrics.monthlyFee * 12;
  const annualReturn = monthlyNetBenefit * 12;
  const roiPercent = annualInvestment > 0 ? (annualReturn / annualInvestment) * 100 : 0;
  const paybackMonths = monthlyNetBenefit > 0 ? annualInvestment / monthlyNetBenefit : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white text-foreground">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" /><span className="text-sm font-medium text-emerald-700">Marcon Benefits Proposal Presentation</span></div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{proposal.company_name}</h1>
            <div className="flex flex-wrap gap-3 text-sm text-slate-600">
              {proposal.company_city && proposal.company_state && <span className="flex items-center gap-1"><Building2 className="w-4 h-4" />{proposal.company_city}, {proposal.company_state}</span>}
              {proposal.contact_email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{proposal.contact_email}</span>}
              {proposal.contact_phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{proposal.contact_phone}</span>}
            </div>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-2">
            <Badge className="bg-emerald-100 text-emerald-700">{proposal.status?.toUpperCase() || 'PROPOSED'}</Badge>
            <span className="text-xs text-slate-400">Last updated: {proposal.updated_at ? new Date(proposal.updated_at).toLocaleString() : 'N/A'}</span>
          </div>
        </div>

        <HowItWorksFlow />
        <AverageEmployeeImpact summary={summary} />
        <KeyMetricsCards summary={summary} />

        <Tabs defaultValue="employees" className="w-full">
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-2 shadow-sm">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto bg-transparent gap-2">
              <TabsTrigger
                value="employees"
                className="min-h-[44px] sm:min-h-[52px] text-sm font-semibold rounded-xl px-3 py-2 sm:px-4 sm:py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Employees
              </TabsTrigger>
              <TabsTrigger
                value="tax-savings"
                className="min-h-[44px] sm:min-h-[52px] text-sm font-semibold rounded-xl px-3 py-2 sm:px-4 sm:py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Tax Savings
              </TabsTrigger>
              <TabsTrigger
                value="your-impact"
                className="min-h-[44px] sm:min-h-[52px] text-sm font-semibold rounded-xl px-3 py-2 sm:px-4 sm:py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Your Impact
              </TabsTrigger>
              <TabsTrigger
                value="comparison"
                className="min-h-[44px] sm:min-h-[52px] text-sm font-semibold rounded-xl px-3 py-2 sm:px-4 sm:py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Comparison
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="employees" className="mt-4">
            <Card><CardHeader><CardTitle>Employee Summary</CardTitle></CardHeader><CardContent><div className="overflow-x-auto rounded-lg border"><Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Employee</TableHead><TableHead className="text-right">Annual Salary</TableHead><TableHead className="text-center">NW4</TableHead><TableHead className="text-center">F</TableHead><TableHead className="text-center">#</TableHead><TableHead className="text-right">$</TableHead><TableHead className="text-center">State</TableHead><TableHead className="text-right">Section 125 / mo</TableHead><TableHead className="text-right">Net Benefit / mo</TableHead><TableHead className="text-right">Empr Gross / mo</TableHead><TableHead className="text-right">Fee / mo</TableHead><TableHead className="text-right">Contrib / mo</TableHead><TableHead className="text-right">Employer Net / mo</TableHead><TableHead className="text-right">Annualized</TableHead></TableRow></TableHeader><TableBody>{flattenedRows.map((row, idx) => {
              const fs = String(row.filing_status || '').toLowerCase();
              const fCode = fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : fs ? 'S' : '-';
              const isPost2020 = row.w4_version === 'post_2020';
              return (
                <TableRow key={row.id || idx} className="hover:bg-emerald-50">
                  <TableCell className="font-medium">{row.full_name}</TableCell>
                  <TableCell className="text-right">{row.annual_salary ? formatCurrency(row.annual_salary) : '-'}</TableCell>
                  <TableCell className="text-center"><span className={`font-semibold ${isPost2020 ? 'text-blue-600' : 'text-slate-500'}`}>{isPost2020 ? 'Y' : row.w4_version ? 'N' : '-'}</span></TableCell>
                  <TableCell className="text-center text-sm font-medium">{fCode}</TableCell>
                  <TableCell className="text-center text-sm">{isPost2020 ? '-' : (row.w4_allowances || '-')}</TableCell>
                  <TableCell className="text-right text-sm">{row.w4_step3_total > 0 ? formatCurrency(row.w4_step3_total) : '-'}</TableCell>
                  <TableCell className="text-center uppercase text-sm">{row.state || '-'}</TableCell>
                  <TableCell className="text-right text-blue-600 font-medium">{row.section125_monthly > 0 ? formatCurrency(row.section125_monthly) : '-'}</TableCell>
                  <TableCell className="text-right text-emerald-600 font-medium">{row.net_flex > 0 ? formatCurrency(row.net_flex) : '-'}</TableCell>
                  <TableCell className="text-right text-slate-600">{row.employer_gross !== 0 ? formatCurrency(row.employer_gross) : '-'}</TableCell>
                  <TableCell className="text-right text-slate-600">{row.fee > 0 ? formatCurrency(row.fee) : '-'}</TableCell>
                  <TableCell className="text-right text-slate-600">{row.contribution > 0 ? formatCurrency(row.contribution) : '-'}</TableCell>
                  <TableCell className="text-right text-purple-600 font-medium">{row.employer_net > 0 ? formatCurrency(row.employer_net) : '-'}</TableCell>
                  <TableCell className="text-right text-purple-600 font-medium">{row.annualized !== 0 ? formatCurrency(row.annualized) : '-'}</TableCell>
                </TableRow>
              );
            })}</TableBody></Table></div></CardContent></Card>
          </TabsContent>

          <TabsContent value="tax-savings" className="mt-4 space-y-6">
            <Card><CardHeader><CardTitle>Tax Savings Breakdown</CardTitle></CardHeader><CardContent className="space-y-4"><div className="grid grid-cols-1 lg:grid-cols-3 gap-4"><div className="rounded-lg border p-4 bg-blue-50 border-blue-200"><p className="font-medium">Federal</p><p className="text-lg font-semibold text-blue-700">{formatCurrency(metrics.monthlyFederal)}/mo</p></div><div className="rounded-lg border p-4 bg-emerald-50 border-emerald-200"><p className="font-medium">FICA</p><p className="text-lg font-semibold text-emerald-700">{formatCurrency(metrics.monthlyFica)}/mo</p></div><div className="rounded-lg border p-4 bg-purple-50 border-purple-200"><p className="font-medium">State</p><p className="text-lg font-semibold text-purple-700">{formatCurrency(metrics.monthlyState)}/mo</p></div></div><div className="rounded-lg border border-slate-200 bg-slate-50 p-4"><p className="font-medium">Combined Tax Savings</p><p className="text-xl font-bold">{formatCurrency(monthlyCombined)}/mo</p><p className="text-sm">{formatCurrency(metrics.annualTotalTaxSavings)}/yr</p><div className="mt-2 space-y-1"><div className="flex justify-between text-sm"><span>Federal</span><span>{formatPercent(metrics.federalPct)}</span></div><div className="flex justify-between text-sm"><span>FICA</span><span>{formatPercent(metrics.ficaPct)}</span></div><div className="flex justify-between text-sm"><span>State</span><span>{formatPercent(metrics.statePct)}</span></div></div></div><div className="overflow-x-auto rounded-lg border"><Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Employee</TableHead><TableHead className="text-right">Federal / mo</TableHead><TableHead className="text-right">FICA / mo</TableHead><TableHead className="text-right">State / mo</TableHead><TableHead className="text-right">Total / mo</TableHead><TableHead className="text-right">Total / yr</TableHead></TableRow></TableHeader><TableBody>{flattenedRows.map((row, idx) => { const total = row.federal_tax_savings + row.fica_tax_savings + row.state_tax_savings; return <TableRow key={row.id || idx} className="hover:bg-emerald-50"><TableCell>{row.full_name}</TableCell><TableCell className="text-right">{formatCurrency(row.federal_tax_savings)}</TableCell><TableCell className="text-right">{formatCurrency(row.fica_tax_savings)}</TableCell><TableCell className="text-right">{formatCurrency(row.state_tax_savings)}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(total)}</TableCell><TableCell className="text-right font-semibold text-emerald-700">{formatCurrency(total * 12)}</TableCell></TableRow>; })}</TableBody></Table></div></CardContent></Card>
          </TabsContent>

          <TabsContent value="your-impact" className="mt-4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Your Financial Impact
                </CardTitle>
                <p className="text-sm text-slate-500">
                  What your company saves from this Section 125 proposal
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const monthlyFicaSavings = rows.reduce((sum, row) => sum + Number(row.fica_tax_savings || 0), 0);
                  const monthlyFee = rows.reduce((sum, row) => sum + Number(row.fee || 0), 0);
                  const monthlyContrib = rows.reduce((sum, row) => sum + Number(row.contribution || 0), 0);
                  const monthlyGrossBenefit = monthlyFicaSavings - monthlyFee;
                  const monthlyNetBenefit = monthlyGrossBenefit - monthlyContrib;
                  const annualInvestment = monthlyFee * 12;
                  const annualReturn = monthlyNetBenefit * 12;
                  const paybackMonths = monthlyNetBenefit > 0 ? (annualInvestment / monthlyNetBenefit) : 0;

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="font-medium text-slate-800 mb-3">Monthly Breakdown</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">FICA Savings</span>
                              <span className="font-medium text-emerald-700">${monthlyFicaSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Admin Fee</span>
                              <span className="font-medium text-slate-700">-${monthlyFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">Gross Benefit</span>
                              <span className="font-semibold text-slate-900">${monthlyGrossBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Contrib</span>
                              <span className="font-medium text-slate-700">-${monthlyContrib.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between rounded bg-emerald-100 px-2 py-1">
                              <span className="font-semibold text-emerald-800">Your Net</span>
                              <span className="font-bold text-emerald-800">${monthlyNetBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="font-medium text-slate-800 mb-3">Annual Breakdown</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">FICA Savings</span>
                              <span className="font-medium text-emerald-700">${(monthlyFicaSavings * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Admin Fee</span>
                              <span className="font-medium text-slate-700">-${(monthlyFee * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">Gross Benefit</span>
                              <span className="font-semibold text-slate-900">${(monthlyGrossBenefit * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Contrib</span>
                              <span className="font-medium text-slate-700">-${(monthlyContrib * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between rounded bg-emerald-100 px-2 py-1">
                              <span className="font-semibold text-emerald-800">Your Net</span>
                              <span className="font-bold text-emerald-800">${(monthlyNetBenefit * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="font-semibold">Employee</TableHead>
                              <TableHead className="text-right font-semibold">FICA Savings</TableHead>
                              <TableHead className="text-right font-semibold">Fee</TableHead>
                              <TableHead className="text-right font-semibold">Contrib</TableHead>
                              <TableHead className="text-right font-semibold">Your Net (Monthly)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((row, idx) => (
                              <TableRow key={row.id || `${row.full_name || 'employee'}-${idx}`}>
                                <TableCell className="font-medium">{row.full_name || 'Unknown'}</TableCell>
                                <TableCell className="text-right text-emerald-700">
                                  ${Number(row.fica_tax_savings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right text-slate-700">
                                  -${Number(row.fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right text-slate-700">
                                  -${Number(row.contribution || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-purple-700">
                                  ${Number(row.employer_net || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                              <TableCell>TOTAL</TableCell>
                              <TableCell className="text-right text-emerald-800">
                                ${monthlyFicaSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-slate-800">
                                -${monthlyFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-slate-800">
                                -${monthlyContrib.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-purple-800">
                                ${monthlyNetBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="font-medium text-slate-800 mb-3">ROI Calculation</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Annual Investment (admin fees)</span>
                            <span className="font-medium text-slate-900">
                              ${annualInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Annual Return (net savings)</span>
                            <span className="font-medium text-emerald-700">
                              ${annualReturn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="h-px bg-slate-200 my-1" />
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-700">Payback Period</span>
                            <span className="font-bold text-emerald-800">
                              {annualInvestment > 0 && monthlyNetBenefit > 0
                                ? `${paybackMonths.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} months`
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comparison" className="mt-4">
            <Card><CardHeader><CardTitle>With vs Without Section 125</CardTitle></CardHeader><CardContent className="space-y-4"><div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><p className="font-medium">Assumptions used</p><ul className="list-disc ml-5 mt-1 space-y-1"><li>"Without Section 125" baseline is modeled as zero tax-savings and zero Section 125-related employer impact.</li><li>"With Section 125" values are sourced from computed census row fields in this proposal.</li><li>Deltas represent incremental impact attributable to Section 125.</li></ul></div><div className="grid md:grid-cols-2 gap-4"><div className="rounded-lg border bg-slate-50 p-4"><p className="font-semibold">Without</p><p>Employee Tax Savings / mo: {formatCurrency(0)}</p><p>Employee Tax Savings / yr: {formatCurrency(0)}</p><p>Employer Net / mo: {formatCurrency(0)}</p><p>Employer Net / yr: {formatCurrency(0)}</p></div><div className="rounded-lg border bg-emerald-50 border-emerald-200 p-4"><p className="font-semibold text-emerald-800">With</p><p>Employee Tax Savings / mo: {formatCurrency(metrics.monthlyEmployeeTaxSavings)}</p><p>Employee Tax Savings / yr: {formatCurrency(metrics.monthlyEmployeeTaxSavings * 12)}</p><p>Employer Net / mo: {formatCurrency(metrics.monthlyEmployerNet)}</p><p>Employer Net / yr: {formatCurrency(metrics.monthlyEmployerNet * 12)}</p></div></div></CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
